import React, { useEffect } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import WhatsAppFloat from '../components/WhatsAppFloat';
import SEO from '../components/SEO';
import './PrivacyPolicy.css';

const PrivacyPolicy = () => {
    useEffect(() => {
        // Force hide spinner immediately
        const spinner = document.getElementById('spinner');
        if (spinner) {
            spinner.classList.remove('show');
            spinner.style.display = 'none';
            spinner.style.opacity = '0';
            spinner.style.visibility = 'hidden';
        }
        if (window.WOW) {
            new window.WOW().init();
        }
    }, []);

    return (
        <>
            <SEO
                title="Privacy Policy | WebBears"
                description="Read the WebBears Privacy Policy to understand how we collect, use, protect and process personal information."
                url="https://www.webbears.in/privacy-policy"
                name="WebBears"
                type="website"
            />

            <WhatsAppFloat />
            <Navbar />

            {/* Page Header Start */}
            <div className="container-fluid page-header mb-5 wow fadeIn" data-wow-delay="0.1s">
                <div className="container">
                    <h1 className="display-3 mb-4 animated slideInDown">Privacy Policy</h1>
                    <nav aria-label="breadcrumb animated slideInDown">
                        <ol className="breadcrumb mb-0">
                            <li className="breadcrumb-item"><a href="/">Home</a></li>
                            <li className="breadcrumb-item active" aria-current="page">Privacy Policy</li>
                        </ol>
                    </nav>
                </div>
            </div>
            {/* Page Header End */}

            {/* Privacy Policy Content Start */}
            <div className="container-xxl py-5 wow fadeInUp" data-wow-delay="0.1s">
                <div className="container">

                    {/* Intro */}
                    <div className="pp-intro">
                        <h2>Your Privacy Matters</h2>
                        <p>
                            At <span className="hl">WebBears</span>, we respect your privacy and are committed to protecting your personal information. This Privacy Policy explains how we collect, use, store, share and protect the information you provide to us when you use our website, services, or communicate with us through any channel. By using our website or services, you acknowledge and agree to the practices described in this Privacy Policy.
                        </p>
                        <span className="pp-last-updated">
                            <i className="fa fa-calendar-alt me-2"></i>
                            Last Updated: 13 August 2025
                        </span>
                    </div>

                    {/* 1. Information We Collect */}
                    <div className="pp-card">
                        <div className="d-flex align-items-center gap-3 mb-3">
                            <span className="pp-num">01</span>
                            <h3>Information We Collect</h3>
                        </div>
                        <p>WebBears may collect information that is voluntarily provided by users when they interact with our website, submit forms, make enquiries, or use our services. This may include:</p>
                        <ul>
                            <li>Name</li>
                            <li>Email address</li>
                            <li>Phone number</li>
                            <li>WhatsApp number</li>
                            <li>Business or company name</li>
                            <li>Service requirements and project details</li>
                            <li>Messages, enquiries, and feedback</li>
                            <li>Information submitted through website forms</li>
                            <li>Technical information such as browser type, device information, and IP address where applicable</li>
                            <li>Cookies and similar technologies where applicable</li>
                        </ul>
                    </div>

                    {/* 2. How We Use Your Information */}
                    <div className="pp-card">
                        <div className="d-flex align-items-center gap-3 mb-3">
                            <span className="pp-num">02</span>
                            <h3>How We Use Your Information</h3>
                        </div>
                        <p>The information we collect may be used for the following purposes:</p>
                        <ul>
                            <li>To respond to your enquiries and requests</li>
                            <li>To provide the services you have requested</li>
                            <li>To communicate with customers, leads, and potential clients</li>
                            <li>To provide customer support and assistance</li>
                            <li>To understand your service requirements and project needs</li>
                            <li>To improve our website, services, and overall user experience</li>
                            <li>To maintain the security and integrity of our systems</li>
                            <li>To prevent fraud, unauthorized activity, or misuse</li>
                            <li>To meet applicable legal or regulatory requirements</li>
                        </ul>
                    </div>

                    {/* 3. WhatsApp Communication */}
                    <div className="pp-card">
                        <div className="d-flex align-items-center gap-3 mb-3">
                            <span className="pp-num">03</span>
                            <h3>WhatsApp Communication</h3>
                        </div>
                        <p>WebBears may use WhatsApp Business and/or WhatsApp Cloud API to communicate with customers and potential customers. When you contact WebBears through WhatsApp or when we communicate with you via WhatsApp, we may receive and process information associated with the conversation, including:</p>
                        <ul>
                            <li>Your WhatsApp phone number</li>
                            <li>Name and profile information where available</li>
                            <li>Messages sent to WebBears</li>
                            <li>Enquiries and service-related information</li>
                            <li>Relevant communication information and timestamps</li>
                        </ul>
                        <div className="pp-info">
                            <i className="fab fa-whatsapp"></i>
                            <span>
                                Please note that WhatsApp is operated by Meta Platforms, Inc. WhatsApp and Meta may independently collect and process information in accordance with their own privacy policies and terms of service. We encourage you to review <a href="https://www.facebook.com/privacy/policies/" target="_blank" rel="noopener noreferrer" style={{ color: '#0277bd', fontWeight: 600 }}>Meta's Privacy Policy</a> for more information.
                            </span>
                        </div>
                    </div>

                    {/* 4. Cookies */}
                    <div className="pp-card">
                        <div className="d-flex align-items-center gap-3 mb-3">
                            <span className="pp-num">04</span>
                            <h3>Cookies</h3>
                        </div>
                        <p>Our website may use cookies and similar technologies where applicable to enhance website functionality, maintain security, support analytics, and improve your browsing experience. Cookies are small data files stored on your device that help us understand how you use our website and allow us to deliver a better, more personalised experience.</p>
                        <p className="mt-2">You can manage your cookie preferences through your browser settings. Please note that disabling cookies may affect certain features or functionality of our website.</p>
                    </div>

                    {/* 5. Data Sharing */}
                    <div className="pp-card">
                        <div className="d-flex align-items-center gap-3 mb-3">
                            <span className="pp-num">05</span>
                            <h3>Data Sharing</h3>
                        </div>
                        <p>WebBears does not sell or rent your personal information to third parties.</p>
                        <p className="mt-2">Your information may be shared with trusted service providers or technology platforms only when reasonably necessary to operate our website, provide the services you have requested, communicate with you, maintain security, or comply with applicable legal requirements. Any such sharing is limited to what is necessary for these purposes.</p>
                    </div>

                    {/* 6. Third-Party Services */}
                    <div className="pp-card">
                        <div className="d-flex align-items-center gap-3 mb-3">
                            <span className="pp-num">06</span>
                            <h3>Third-Party Services</h3>
                        </div>
                        <p>WebBears may use third-party platforms and services, including communication and infrastructure providers, to operate our website and deliver our services. This may include services provided by Meta and WhatsApp, among others.</p>
                        <p className="mt-2">These third-party services have their own privacy policies and terms of service. We recommend reviewing the privacy practices of any third-party service you interact with, as WebBears is not responsible for the privacy practices of external platforms.</p>
                    </div>

                    {/* 7. Data Security */}
                    <div className="pp-card">
                        <div className="d-flex align-items-center gap-3 mb-3">
                            <span className="pp-num">07</span>
                            <h3>Data Security</h3>
                        </div>
                        <p>WebBears takes reasonable technical and organisational measures to protect your personal information from unauthorised access, misuse, alteration, disclosure, or destruction. We strive to maintain appropriate safeguards to keep your data secure.</p>
                        <div className="pp-info">
                            <i className="fa fa-shield-alt"></i>
                            <span>While we take reasonable steps to protect your information, no method of transmission over the internet or method of electronic storage is completely secure. We cannot guarantee absolute security of your data.</span>
                        </div>
                    </div>

                    {/* 8. Data Retention */}
                    <div className="pp-card">
                        <div className="d-flex align-items-center gap-3 mb-3">
                            <span className="pp-num">08</span>
                            <h3>Data Retention</h3>
                        </div>
                        <p>We may retain your information for as long as reasonably necessary for the purposes described in this Privacy Policy, including but not limited to providing services, responding to enquiries, supporting business operations, fulfilling legal requirements, resolving disputes, and maintaining records.</p>
                    </div>

                    {/* 9. Your Privacy Rights */}
                    <div className="pp-card">
                        <div className="d-flex align-items-center gap-3 mb-3">
                            <span className="pp-num">09</span>
                            <h3>Your Privacy Rights</h3>
                        </div>
                        <p>Where applicable, you may have the right to request access to, correction of, or deletion of your personal information held by WebBears. If you wish to exercise any of these rights or have any privacy-related requests, please contact us using the details provided below.</p>
                        <p className="mt-2">We will respond to your request within a reasonable timeframe and in accordance with applicable laws and regulations.</p>
                    </div>

                    {/* 10. Children's Privacy */}
                    <div className="pp-card">
                        <div className="d-flex align-items-center gap-3 mb-3">
                            <span className="pp-num">10</span>
                            <h3>Children's Privacy</h3>
                        </div>
                        <p>WebBears does not knowingly collect personal information from children where prohibited by applicable law. If you believe that we have inadvertently collected information from a child, please contact us immediately so that we can take appropriate steps to remove such information.</p>
                    </div>

                    {/* 11. Changes to This Privacy Policy */}
                    <div className="pp-card">
                        <div className="d-flex align-items-center gap-3 mb-3">
                            <span className="pp-num">11</span>
                            <h3>Changes to This Privacy Policy</h3>
                        </div>
                        <p>WebBears may update this Privacy Policy from time to time to reflect changes in our practices, services, or applicable laws. The latest version of the Privacy Policy will always be published on this page. We encourage you to review this page periodically to stay informed about how we protect your information.</p>
                    </div>

                    {/* 12. Contact Us */}
                    <div className="pp-contact-card">
                        <h3>Contact Us</h3>
                        <p>If you have any questions, concerns, or requests regarding this Privacy Policy or how we handle your personal information, please do not hesitate to contact us.</p>
                        <div className="pp-contact-methods">
                            <a className="pp-contact-tag" href="mailto:info@webbears.in">
                                <i className="fa fa-envelope me-2"></i>info@webbears.in
                            </a>
                            <a className="pp-contact-tag" href="tel:+918408800193">
                                <i className="fa fa-phone-alt me-2"></i>+91 8408800193
                            </a>
                            <a className="pp-contact-tag" href="https://wa.link/erzrv5" target="_blank" rel="noopener noreferrer">
                                <i className="fab fa-whatsapp me-2"></i>WhatsApp
                            </a>
                            <a className="pp-contact-tag" href="/contact">
                                <i className="fa fa-globe me-2"></i>Contact Page
                            </a>
                        </div>
                    </div>

                </div>
            </div>
            {/* Privacy Policy Content End */}

            <Footer />
        </>
    );
};

export default PrivacyPolicy;
